import { BarChart3, Coins, MessagesSquare, Network, Timer, UserPlus, Users } from "lucide-react";
import { AFFILIATE_LEVELS } from "../../data/site";
import { useApi, usd } from "../../dashboard/api";
import { ActivityFeed } from "../../components/dashboard/ActivityFeed";
import { EmptyState, ErrorState, Panel, Skeleton } from "../../components/dashboard/ui";

const TeamPage = ({ kind }) => {
    const { data, loading, error } = useApi("team");
    const isPartners = kind === "partners";
    return (
        <Panel title={isPartners ? "Partners" : "Team"} testId={`${kind}-page`}>
            {loading ? (
                <div className="space-y-2">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                </div>
            ) : error ? (
                <ErrorState testId={`${kind}-error`} />
            ) : (
                <EmptyState
                    icon={isPartners ? UserPlus : Users}
                    title={isPartners ? "No partners yet" : "No team members yet"}
                    desc={
                        isPartners
                            ? "Your partner activity will appear here once your personal link is used."
                            : "Your team activity will appear here as your network grows."
                    }
                    testId={`${kind}-empty`}
                />
            )}
        </Panel>
    );
};

const AffiliatePage = () => (
    <Panel title="Affiliate Distribution" testId="affiliate-page">
        <p className="text-xs leading-relaxed text-nx-mute">
            50% of every level price flows into affiliate income across seven upline levels.
        </p>
        <div className="mt-5 space-y-3.5">
            {AFFILIATE_LEVELS.map((l, i) => (
                <div key={l.level} data-testid={`affiliate-page-${l.level.toLowerCase()}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="font-extrabold text-nx-sub">{l.level}</span>
                        <span className="font-extrabold text-nx-lime tabular">{l.pct}%</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-nx-soft">
                        <div
                            className={`nx-bar h-full rounded-full ${i === 0 ? "bg-nx-lime" : "bg-nx-lime/50"}`}
                            style={{ "--bar-w": `${(l.pct / 40) * 100}%`, animationDelay: `${i * 0.08}s` }}
                        />
                    </div>
                </div>
            ))}
        </div>
        <div className="mt-6 border-t border-white/5 pt-5">
            <EmptyState
                icon={Network}
                title="No affiliate income yet"
                desc="Your affiliate income records will appear here."
                testId="affiliate-empty"
            />
        </div>
    </Panel>
);

const StatsPage = () => {
    const { data, loading, error } = useApi("summary");
    if (loading) return <Skeleton className="h-64 w-full rounded-xl" />;
    if (error || !data) return <ErrorState testId="stats-error" />;
    const { financial, platform } = data;
    const items = [
        { label: "Total Profit", value: usd(financial.totalProfit) },
        { label: "Profit Ratio", value: `${financial.profitRatio}%` },
        { label: "Est. Monthly Salary", value: usd(financial.estMonthlySalary) },
        { label: "USDT Balance", value: Number(financial.usdtBalance).toFixed(2) },
        { label: "Salary Fund", value: usd(financial.salaryFund) },
        { label: "Token Fund", value: usd(financial.tokenFund) },
        { label: "Members Total", value: platform.membersTotal },
        { label: "Total USDT Paid", value: usd(platform.totalUsdtPaid) },
    ];
    return (
        <Panel title="Statistics" padded={false} testId="stats-page">
            <div className="grid grid-cols-2 divide-x divide-y divide-white/5 lg:grid-cols-4 lg:divide-y-0">
                {items.map((m) => (
                    <div key={m.label} className="px-5 py-5" data-testid={`stat-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-nx-mute">{m.label}</p>
                        <p className="mt-1.5 text-lg font-extrabold text-nx-text tabular">{m.value}</p>
                    </div>
                ))}
            </div>
        </Panel>
    );
};

const COMING_SOON = {
    marathon: { icon: Timer, title: "Marathon — Coming Soon" },
    social: { icon: MessagesSquare, title: "Social — Coming Soon" },
    tokens: { icon: Coins, title: "Tokens — Coming Soon" },
};

const ComingSoonPage = ({ kind }) => {
    const cfg = COMING_SOON[kind];
    return (
        <Panel title={cfg.title.split(" — ")[0]} testId={`${kind}-page`}>
            <EmptyState
                icon={cfg.icon}
                title="Coming soon"
                desc="This ecosystem module is listed as Coming Soon in the Nexora roadmap."
                testId={`${kind}-empty`}
            />
        </Panel>
    );
};

export const SimplePage = ({ kind }) => {
    if (kind === "team" || kind === "partners") return <TeamPage kind={kind} />;
    if (kind === "affiliate") return <AffiliatePage />;
    if (kind === "stats") return <StatsPage />;
    if (kind === "activity") return <ActivityFeed />;
    return <ComingSoonPage kind={kind} />;
};

export const statsIcon = BarChart3;
